import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, Input, Modal, ModalBody, ModalHeader, Tooltip } from 'reactstrap'
import agency from '../../../../api/agency/hooks'
import images from '../../../../api/images/hooks'
import properties from '../../../../api/properties/hooks'
import { PropertyTranslations } from '../../../../api/properties/types/response'
import { MODAL_TYPES } from '../../../../common/constants'
import { hideModal, showModal } from '../../../../store/modal'
import { resetTableCheckbox } from '../../../../store/table'
import { templateTranslations } from '../translations'
import { ShopWindowPrintProperty, TemplatesModalProps } from '../types'
import styles from './TemplatesModal.module.css'

const TemplatesModal = (props: TemplatesModalProps) => {
    const currentLang = localStorage.getItem('i18nextLng') || 'hr'
    const alternativeLang = currentLang === 'en' ? 'hr' : 'en'

    const [lang, setLang] = useState(currentLang)
    const [tooltipOpen, setTooltipOpen] = useState<Record<string, boolean>>({})

    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { TEMPLATES_MODAL, SHOPWINDOW_PRINT_MODAL } = MODAL_TYPES
    const { isOpen, templatesModal } = props
    const { selectedProperties } = templatesModal

    const propertyIds = (selectedProperties?.map((property) => property.id) as number[]) || []

    const imagesConfig = {
        params: {
            parent: propertyIds,
            collection: 'photo',
            module: 'property',
        },
        isEnabled: { enabled: !!propertyIds && propertyIds.length > 0 },
    }

    const { data: photos } = images.GetImages.useQuery(imagesConfig.params, imagesConfig.isEnabled)
    const { data: agencyData, isLoading } = agency.GetAgency.useQuery()
    const propertyQueries = properties.GetPropertiesById.useQueries(propertyIds)

    const propertiesLoading = propertyQueries.some((query) => query.isLoading)

    const translationsById = useMemo(() => {
        return propertyQueries.reduce(
            (acc, query) => {
                const property = query.data?.item
                if (property) acc[property.id] = property.translations

                return acc
            },
            {} as Record<number, PropertyTranslations>
        )
    }, [propertyQueries])

    const formattedProperties = useMemo(() => {
        if (!photos || !selectedProperties) return selectedProperties

        return selectedProperties.map((property) => ({
            ...property,
            photos: photos.filter((photo) => photo.parent === property.id),
        }))
    }, [selectedProperties, photos])

    const templateOptions = [
        { id: 'template-1', src: '../images/templates/template-1.png' },
        { id: 'template-2', src: '../images/templates/template-2.png' },
        { id: 'template-3', src: '../images/templates/template-3.png' },
        { id: 'template-4', src: '../images/templates/template-4.png' },
        { id: 'template-5', src: '../images/templates/template-5.png' },
        { id: 'template-6', src: '../images/templates/template-6.png' },
        { id: 'template-7', src: '../images/templates/template-7.png' },
        { id: 'template-8', src: '../images/templates/template-8.png' },
    ]

    const propertyPrintData = () => {
        const propertyData = formattedProperties?.map((property) => {
            const { id, internalAppId, photos, area, bedrooms, floorNo, price, propertyService, propertyType } = property
            const translations = translationsById[id as number]

            return {
                id,
                internalAppId,
                photos,
                area,
                areaLabel: templateTranslations[lang].areaLabel,
                bedrooms,
                bedroomsLabel: templateTranslations[lang].bedroomsLabel,
                description: translations?.[lang]?.propertyDescription ?? '',
                floorNo,
                logo: agencyData?.logo,
                phoneNumber: agencyData?.phoneNumber,
                price,
                qrCode: agencyData?.qrCode,
                service: {
                    id: propertyService.id,
                    label: templateTranslations[lang].propertyService[propertyService.id],
                },
                title: translations?.[lang]?.propertyTitle ?? '',
                type: {
                    id: propertyType.id,
                    label: templateTranslations[lang].propertyType[propertyType.id],
                },
                website: agencyData?.websiteUrl,
            } as ShopWindowPrintProperty
        })

        return propertyData ?? []
    }

    const handleShopWindowPrint = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isLoading || propertiesLoading) return

        const selectedTemplate = e.currentTarget.id
        const isTemplateEnabled = handleEnabledTemplates(selectedTemplate)

        if (isTemplateEnabled) {
            const propertyData = propertyPrintData()

            const payload = {
                modalType: SHOPWINDOW_PRINT_MODAL,
                modalProps: {
                    properties: propertyData,
                    selectedTemplate: selectedTemplate,
                },
            }

            if (propertyData.length) {
                dispatch(showModal(payload))
            }
        }
    }

    const handleClose = () => {
        dispatch(hideModal({ modalType: TEMPLATES_MODAL }))
        dispatch(resetTableCheckbox(true))
    }

    const handleEnabledTemplates = (template: string) => {
        switch (template) {
            case 'template-1':
            case 'template-2':
            case 'template-3':
            case 'template-5':
            case 'template-6':
                return formattedProperties?.length === 1
            case 'template-4':
                return formattedProperties?.length === 2
            case 'template-7':
                return formattedProperties?.length === 4
            case 'template-8':
                return formattedProperties?.length === 6
            default:
                return false
        }
    }

    const handleLangChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLang(e.target.value)
    }

    const toggleTooltip = (tooltipId: string) => {
        setTooltipOpen((prev) => ({
            ...prev,
            [tooltipId]: !prev[tooltipId],
        }))
    }

    const tooltipContent = (tooltipId: string) => {
        switch (tooltipId) {
            case 'template-1':
            case 'template-2':
            case 'template-3':
            case 'template-5':
            case 'template-6':
                return t('modal.templates.tooltip.selectOne')
            case 'template-4':
                return t('modal.templates.tooltip.selectTwo')
            case 'template-7':
                return t('modal.templates.tooltip.selectFour')
            case 'template-8':
                return t('modal.templates.tooltip.selectSix')
            default:
                break
        }
    }

    return (
        <Modal isOpen={isOpen} className={styles.templatesModal} centered>
            <div className={styles.modalHeaderContainer}>
                <ModalHeader>{t('app.common.template')}</ModalHeader>
                <Input onChange={handleLangChange} value={lang} name="select" type="select">
                    <option value={currentLang}>{currentLang}</option>
                    <option value={alternativeLang}>{alternativeLang}</option>
                </Input>
                <Button type="button" className={styles.btnClose} onClick={handleClose}>
                    <i className="mdi mdi-close fs-16"></i>
                </Button>
            </div>
            <ModalBody>
                <div className={styles.gridContainer}>
                    {templateOptions.map((option, index) => {
                        const showTooltip = !handleEnabledTemplates(option.id)

                        return (
                            <React.Fragment key={option.id}>
                                {showTooltip && (
                                    <Tooltip isOpen={tooltipOpen[option.id]} toggle={() => toggleTooltip(option.id)} target={option.id}>
                                        {tooltipContent(option.id)}
                                    </Tooltip>
                                )}
                                <div
                                    id={option.id}
                                    data-testid={option.id}
                                    onClick={handleShopWindowPrint}
                                    className={`${styles.gridItem} 
                                        ${index < 4 ? styles.gridItemVertical : styles.gridItemHorizontal} 
                                        ${!handleEnabledTemplates(option.id) && styles.gridItemDisabled}
                                        `}
                                >
                                    <img src={option.src} id={option.id} alt={option.id} />
                                </div>
                            </React.Fragment>
                        )
                    })}
                </div>
            </ModalBody>
        </Modal>
    )
}

export default TemplatesModal
